// Board chrome: tabs, the round bar, the "Needs you now" strip, the learning offer and the card
// rows. Counts appear only as "N in this round" (invariant 8: no shame counters, no red). The
// words say "round" for a pass and "checked" for a sweep: the code's names stay as they are.
import { memo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, BookOpen, Focus, Gauge, Pin, SlidersHorizontal, Sparkles } from "lucide-react";
import { toast } from "sonner";
import type { BoardView, CardSummary } from "@shared/api";
import type { RoundOrder } from "@shared/startEasily";
import { isApiError } from "@web/lib/api";
import { cn } from "@web/lib/cn";
import { feedApi } from "@web/lib/feedApi";
import { feedKeys } from "@web/lib/feedQueries";
import { formatAgo } from "@web/lib/time";
import { useNow } from "@web/lib/useNow";
import { AccountChip } from "@web/ui/AccountChip";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { BOARD_TABS, isToReview, TAB_LABEL, type BoardSections, type BoardTab } from "./selectors";
import { plainText } from "./card/markdownLite";
import { ReplyBadge } from "./ReplyBadge";
import { RoundEstimateText, RowEffort, RowFocusBlock } from "./RoundStart";
import { URGENCY_LABEL, waitingLabel, workNeedsDecision, workPhaseLabel } from "./wording";

/**
 * A calm note once today's spend reaches the soft cap; nothing before that. The amount itself
 * lives in Settings, not in the feed. Phones have no room for it in the tab strip; the round's
 * status line says the same there ("The AI budget is running low today…").
 */
export function PacingNote({ budget }: { budget: BoardView["budget"] | undefined }) {
  if (!budget?.throttled) return null;
  const paused = budget.spentUsd >= budget.hardUsd;
  return (
    <Link
      to="/settings/spend"
      title={
        paused
          ? "Automatic AI review is paused at today's limit. Gmail still syncs and your own requests still run. See spend in Settings."
          : "Claude is pacing itself for the rest of today, so cards may be shorter until tomorrow. See spend in Settings."
      }
      data-testid="pacing-note"
      className="hidden h-8 items-center gap-1.5 rounded-lg px-2 text-sm text-note-ink hover:bg-sunk sm:inline-flex"
    >
      <Gauge aria-hidden className="size-4" />
      <span className="sr-only xl:not-sr-only">
        {paused ? "AI review paused today" : "Pacing itself today"}
      </span>
    </Link>
  );
}

const HEADER_LINK =
  "mm-tap inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-sm text-muted hover:bg-sunk hover:text-ink";

export function BoardTabs({
  tab,
  onTab,
  sections,
  budget,
}: {
  tab: BoardTab;
  onTab: (tab: BoardTab) => void;
  sections: BoardSections | null;
  budget?: BoardView["budget"] | undefined;
}) {
  const needsDecision =
    sections?.working.some(
      (c) => c.status === "blocked" || (c.work && workNeedsDecision(c.work)),
    ) ?? false;
  return (
    // Sized to fit without scrolling from a phone up: labels give way to icons first.
    <div className="mm-scroll-x flex h-12 shrink-0 items-end gap-2 border-b border-line px-3 whitespace-nowrap sm:px-4 md:px-6 lg:gap-5">
      <div role="tablist" aria-label="Board" className="flex items-end gap-3 lg:gap-5">
        {BOARD_TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={t === tab}
            onClick={() => onTab(t)}
            className={cn(
              "flex h-12 items-center gap-1.5 border-b-2 px-0.5 text-sm lg:text-[15px]",
              t === tab
                ? "border-ink font-medium text-ink"
                : "border-transparent text-muted hover:text-ink",
            )}
          >
            {TAB_LABEL[t]}
            {t === "working" && needsDecision ? (
              <span className="flex items-center gap-1 text-xs text-note-ink">
                <span aria-hidden className="size-1.5 rounded-full bg-note-ink" />
                <span className="sr-only lg:not-sr-only">needs a decision</span>
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <div className="ml-auto flex h-12 items-center gap-1">
        <PacingNote budget={budget} />
        <Link to="/focus" title="Focus (F)" className={HEADER_LINK}>
          <Focus aria-hidden className="size-4" />
          <span className="sr-only lg:not-sr-only">Focus</span>
          <Kbd className="hidden lg:inline-flex">F</Kbd>
        </Link>
        {/* On a phone these live under More. */}
        <Link
          to="/feed/config"
          title="How Claude works"
          className={cn(HEADER_LINK, "max-sm:hidden")}
        >
          <SlidersHorizontal aria-hidden className="size-4" />
          <span className="sr-only xl:not-sr-only">How Claude works</span>
        </Link>
        <Link to="/learning" title="Learning" className={cn(HEADER_LINK, "max-sm:hidden")}>
          <Sparkles aria-hidden className="size-4" />
          <span className="sr-only xl:not-sr-only">Learning</span>
        </Link>
        <Link to="/answers" title="Saved answers" className={cn(HEADER_LINK, "max-sm:hidden")}>
          <BookOpen aria-hidden className="size-4" />
          <span className="sr-only xl:not-sr-only">Saved answers</span>
        </Link>
      </div>
    </div>
  );
}

export function useNextPass(feedId: string | null) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  /** `quick_first` (U3) is chosen here, once, and the server keeps it for the whole round. */
  const start = async (order?: RoundOrder) => {
    if (!feedId || busy) return;
    setBusy(true);
    try {
      const board = await feedApi.nextPass(feedId, order);
      queryClient.setQueryData(feedKeys.board(feedId), board);
      void queryClient.invalidateQueries({ queryKey: feedKeys.feeds });
      // Short and dismissible: it must not sit over a card's approve strip.
      toast(
        order === "quick_first" ? "Next round started. Quick ones first." : "Next round started.",
        {
          id: "pass-started",
          duration: 1800,
          closeButton: true,
        },
      );
    } catch (error) {
      toast(
        isApiError(error, "stale")
          ? "The round changed in the meantime. Showing the latest."
          : "Couldn't start the next round. Try again.",
      );
      void queryClient.invalidateQueries({ queryKey: feedKeys.board(feedId) });
    } finally {
      setBusy(false);
    }
  };
  return { start, busy };
}

export function PassBar({
  board,
  count,
  roundCards,
  progress,
}: {
  board: BoardView;
  count: number;
  /** This round's cards, for "about 12 minutes" (U3). */
  roundCards: readonly CardSummary[];
  progress: { decided: number; total: number };
}) {
  const now = useNow(30_000);
  const pct = progress.total ? Math.round((progress.decided / progress.total) * 100) : 0;
  return (
    <div className="flex flex-col gap-2 px-4 pt-3 md:px-6">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* At the end of a round the list itself says so and offers the next one. */}
        {count ? (
          <>
            <p className="font-serif text-[15px] font-semibold text-ink">{`${count} in this round`}</p>
            <RoundEstimateText
              cards={roundCards}
              cleanups={Math.max(0, count - roundCards.length)}
            />
          </>
        ) : null}
        {board.sweep.statusMessage || board.sweep.lastSweepAt ? (
          <p className="text-sm text-muted">
            {board.sweep.statusMessage ?? `Last checked ${formatAgo(board.sweep.lastSweepAt, now)}`}
          </p>
        ) : null}
      </div>
      {progress.total > 0 ? (
        <div
          role="progressbar"
          aria-label="Progress through this round"
          aria-valuemin={0}
          aria-valuemax={progress.total}
          aria-valuenow={progress.decided}
          className="h-1 overflow-hidden rounded-full bg-sunk"
        >
          <div
            className="h-full rounded-full bg-calm transition-[width]"
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}

export function NeedsYouNow({
  cards,
  onOpen,
}: {
  cards: readonly CardSummary[];
  onOpen: (card: CardSummary) => void;
}) {
  if (!cards.length) return null;
  return (
    <section
      aria-label="Needs you now"
      className="mx-4 mt-3 rounded-xl border border-note-line bg-note px-3 py-2 md:mx-5"
    >
      <p className="mm-kicker mb-1 !text-note-ink">Needs you now</p>
      <ul className="flex flex-col gap-1">
        {cards.map((card) => (
          <li key={card.id}>
            <button
              type="button"
              onClick={() => onOpen(card)}
              className="group flex w-full items-center gap-2 rounded-md px-1 py-0.5 text-left text-sm text-note-ink hover:bg-paper/50"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{card.title}</span>
                {card.breakthroughRule ? (
                  <span className="block truncate text-xs opacity-80">{card.breakthroughRule}</span>
                ) : null}
              </span>
              <ArrowRight
                aria-hidden
                className="size-3.5 shrink-0 opacity-60 group-hover:opacity-100"
              />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function LearningOffer({ feedId }: { feedId: string }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const answer = async (accept: boolean) => {
    setBusy(true);
    try {
      await feedApi.learning(feedId, accept);
      toast(
        accept
          ? "Claude will propose what it learned. You'll review it in Learning before anything changes."
          : "Okay. Nothing will change.",
      );
    } catch (error) {
      toast(
        isApiError(error, "not_available")
          ? "Learning isn't wired up yet."
          : "Couldn't record that. Try again.",
      );
    } finally {
      setBusy(false);
      void queryClient.invalidateQueries({ queryKey: feedKeys.board(feedId) });
    }
  };
  return (
    <div className="mx-4 mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-line bg-paper px-4 py-2.5 md:mx-5">
      <Sparkles aria-hidden className="size-4 text-accent-text" />
      <p className="min-w-0 flex-1 text-sm text-ink">Want me to remember what I learned?</p>
      <div className="flex gap-2">
        <Button size="sm" variant="primary" disabled={busy} onClick={() => void answer(true)}>
          Yes
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => void answer(false)}>
          No
        </Button>
      </div>
    </div>
  );
}

export function rowId(cardId: string): string {
  return `card-row-${cardId}`.replace(/[^\w-]/g, "_");
}

export const CardRow = memo(function CardRow({
  card,
  selected,
  showAccount,
  colorIndex,
  accountLabel,
  now,
  onSelect,
}: {
  card: CardSummary;
  selected: boolean;
  showAccount: boolean;
  colorIndex: number;
  accountLabel: string | undefined;
  now: number;
  onSelect: (card: CardSummary) => void;
}) {
  const review = isToReview(card);
  const waiting = review ? waitingLabel(card.waitingSinceAt, now) : null;
  const decision = card.status === "blocked" || (card.work ? workNeedsDecision(card.work) : false);
  const workWords = card.work ? workPhaseLabel(card.work) : null;
  const showWork =
    card.status === "queued" || card.status === "working" || card.status === "blocked";
  return (
    <div
      id={rowId(card.id)}
      role="option"
      aria-selected={selected}
      onClick={() => onSelect(card)}
      className={cn(
        "relative flex cursor-pointer flex-col gap-0.5 border-b border-line px-4 py-3 select-none md:px-5",
        selected ? "bg-accent-soft" : "bg-paper hover:bg-sunk/50",
      )}
    >
      {selected ? (
        <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-accent" />
      ) : null}
      <div className="flex min-w-0 items-center gap-2">
        {card.breakthrough ? (
          <span aria-label="Needs you now" className="size-1.5 shrink-0 rounded-full bg-note-ink" />
        ) : null}
        {card.pinned ? <Pin aria-label="Pinned" className="size-3 shrink-0 text-muted" /> : null}
        <span className="mm-kicker truncate !text-[10px]">{card.eyebrow}</span>
        {card.status === "to_review_updated" ? (
          <span className="shrink-0 text-[11px] text-accent-text">Updated</span>
        ) : null}
        {review ? <ReplyBadge threadKey={card.threadKey} /> : null}
      </div>
      <p className="line-clamp-2 font-serif text-[16px] leading-snug font-semibold text-ink">
        {card.title}
      </p>
      <p className="line-clamp-2 text-sm text-muted">{plainText(card.why)}</p>
      <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
        {showAccount && card.accountEmail ? (
          <AccountChip email={card.accountEmail} label={accountLabel} colorIndex={colorIndex} />
        ) : null}
        {showWork && workWords ? (
          <span className={cn("line-clamp-2", decision && "text-note-ink")}>{workWords}</span>
        ) : null}
        {review && (card.urgency === "now" || card.urgency === "today") ? (
          <span>{URGENCY_LABEL[card.urgency]}</span>
        ) : null}
        {waiting ? <span>{waiting}</span> : null}
        {review ? <RowEffort card={card} /> : <RowFocusBlock card={card} now={now} />}
        {review && card.nextThing ? (
          <span className="truncate text-ink-2">→ {card.nextThing}</span>
        ) : null}
      </div>
    </div>
  );
});
